import { useEffect, useState } from 'react';
import { ChevronLeft, Plus } from 'lucide-react';
import type { OutgoingAlbum } from '../api/client.ts';
import { MAX_ALBUM_ITEMS, type PickedItem } from '../lib/album.ts';
import { formatAttachmentCount, formatFileCount, formatFileSize } from '../lib/format.ts';
import { expectedUploadBytes } from '../lib/videoCompress.ts';
import { AlbumGrid } from './AlbumBubble.tsx';
import { SendIcon } from './MediaIcons.tsx';
import './AlbumCompose.css';

/**
 * What happens between picking photos and sending them: the grouping they will
 * have, an ✕ on each to change your mind, and one caption for all of them.
 *
 * The preview is not a mock-up of the message — it is the message's own grid
 * component with the same layout table behind it (see AlbumBubble), which is the
 * whole reason this screen is worth having. Dropping an item re-lays the group
 * out immediately, so «what will these look like grouped» is answered before the
 * upload rather than after it.
 *
 * The caption is one field for the whole group and there is nowhere to put a
 * second: a per-photo caption is a different feature, and the handoff is
 * explicit that this is not it.
 */
export function AlbumCompose({
  items,
  onAdd,
  onRemove,
  onCancel,
  onSend,
  /** How many of the last pick did not fit, so the cap explains itself. */
  dropped,
  maxUploadBytes,
}: {
  items: PickedItem[];
  /** Opens the system picker again — this app's version of «назад к выбору». */
  onAdd: () => void;
  onRemove: (index: number) => void;
  onCancel: () => void;
  onSend: (album: OutgoingAlbum) => void;
  dropped: number;
  /** The server's per-attachment cap, delivered with the session. */
  maxUploadBytes: number;
}) {
  const [caption, setCaption] = useState('');

  /**
   * Videos whose frame and length are still being read on this device.
   *
   * They hold the send button, and that is the point of knowing: a group sent
   * without a poster is a video with no picture in the thread forever — the
   * server has no decoder to make one after the fact (see lib/videoFrame.ts).
   * Waiting here is a second or two with a spinner on the tile that is doing it;
   * not waiting is a permanent grey square.
   */
  const reading = items.filter((item) => item.reading).length;

  /**
   * Anything the server is going to refuse, named before it is sent.
   *
   * A clip off a phone is routinely bigger than the cap, and the alternative is
   * that the whole group uploads and then comes back «не отправлено» with
   * nothing on screen saying which of the ten was the problem.
   *
   * Measured against what will be uploaded rather than what was picked: a video
   * is re-encoded on the way out (see lib/videoCompress.ts), so the 300 МБ file
   * in the gallery is a 40 МБ upload and refusing it here would be refusing a
   * message this app can send perfectly well.
   *
   * A clip still being read is not judged at all: its length is what the
   * estimate turns on, and until it is known the answer would be the file's own
   * size — which is how a two-minute video gets accused of being 300 МБ for a
   * second and then quietly cleared.
   */
  const tooBig = items.filter(
    (item) => !item.reading && expectedUploadBytes(item) > maxUploadBytes,
  );

  // Escape closes it, as it closes every other sheet in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const full = items.length >= MAX_ALBUM_ITEMS;

  return (
    <div className="album-compose" role="dialog" aria-modal="true" aria-label="Вложения">
      <header className="album-compose-header">
        <button
          type="button"
          className="icon-button album-compose-back"
          onClick={onCancel}
          aria-label="Назад"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="album-compose-title">{formatAttachmentCount(items.length)}</h2>
        <button
          type="button"
          className="album-compose-add"
          onClick={onAdd}
          disabled={full}
          aria-label="Добавить"
        >
          <Plus size={16} />
          Добавить
        </button>
      </header>

      <div className="album-compose-body">
        <AlbumGrid
          items={items.map((item) => ({
            src: item.previewUrl,
            kind: item.kind,
            durationMs: item.durationMs,
            pending: item.reading,
          }))}
          onRemove={onRemove}
        />
      </div>

      {/* Said in words as well as by the spinners, because what it is really
          explaining is why the send button will not press. */}
      {reading > 0 && (
        <p className="album-compose-note" role="status">
          {reading === 1 ? 'Читаем видео…' : `Читаем ${reading} видео…`}
        </p>
      )}

      {/* Only when something was actually left out. The cap is nobody's business
          until it bites. */}
      {reading === 0 && (dropped > 0 || full) && (
        <p className="album-compose-note" role="status">
          {dropped > 0
            ? `Больше ${MAX_ALBUM_ITEMS} за раз нельзя — лишние не добавлены`
            : `Больше ${MAX_ALBUM_ITEMS} за раз нельзя`}
        </p>
      )}

      {tooBig.length > 0 && (
        <p className="album-compose-note album-compose-note-bad" role="alert">
          {tooBig.length === 1
            ? `«${tooBig[0]!.file.name}» больше ${formatFileSize(maxUploadBytes)} — уберите его`
            : `${formatFileCount(tooBig.length)} больше ${formatFileSize(maxUploadBytes)} — уберите их`}
        </p>
      )}

      <form
        className="album-compose-footer"
        onSubmit={(event) => {
          event.preventDefault();
          if (items.length > 0 && tooBig.length === 0 && reading === 0) onSend({ items, caption });
        }}
      >
        <input
          className="album-compose-caption"
          value={caption}
          onChange={(event) => setCaption(event.target.value)}
          placeholder="Добавьте подпись"
          aria-label="Подпись"
        />
        <button
          type="submit"
          className="album-compose-send"
          disabled={items.length === 0 || tooBig.length > 0 || reading > 0}
          aria-label="Отправить"
        >
          <SendIcon size={19} />
        </button>
      </form>
    </div>
  );
}
